import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

const APP_VERSION = '2025.01.08.001';

export const useCacheManager = () => {
  const [isClearing, setIsClearing] = useState(false);
  const [cacheInfo, setCacheInfo] = useState<{
    version: string;
    userAgent: string;
    timestamp: string;
    isAndroid: boolean;
    isSamsung: boolean;
  } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const userAgent = navigator.userAgent;
    const isAndroid = /Android/i.test(userAgent);
    const isSamsung = /Samsung/i.test(userAgent);
    
    setCacheInfo({
      version: APP_VERSION,
      userAgent,
      timestamp: new Date().toISOString(),
      isAndroid,
      isSamsung
    });

    // Log device info for debugging
    console.log('📱 Device Info:', {
      version: APP_VERSION,
      userAgent,
      isAndroid,
      isSamsung,
      timestamp: new Date().toISOString()
    });

    // Check for version mismatches
    const storedVersion = localStorage.getItem('app_version');
    if (storedVersion && storedVersion !== APP_VERSION) {
      console.log('⚠️ Version mismatch detected:', {
        stored: storedVersion,
        current: APP_VERSION
      });
      
      // Force cache clear for Samsung devices specifically
      if (isSamsung) {
        console.log('🔄 Samsung device detected, performing cache clear...');
        clearAllCaches();
      }
    }
    
    localStorage.setItem('app_version', APP_VERSION);
  }, []);

  const clearAllCaches = async () => {
    setIsClearing(true);
    
    try {
      // Clear browser caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('🧹 Browser caches cleared:', cacheNames);
      }

      // Clear localStorage
      const keysToKeep = ['app_version', 'age_verification'];
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      // Clear sessionStorage
      sessionStorage.clear();

      // Update service worker
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.unregister();
          await navigator.serviceWorker.register('/sw.js');
          console.log('🔄 Service Worker re-registered');
        }
      }

      toast({
        title: "Cache Cleared",
        description: "All caches cleared. Refreshing page...",
      });

      // Force hard reload
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('❌ Cache clear failed:', error);
      toast({
        title: "Cache Clear Failed",
        description: "Please manually refresh the page",
        variant: "destructive"
      });
    } finally {
      setIsClearing(false);
    }
  };

  const forceUpdate = () => {
    console.log('🔄 Force update requested');
    
    // Add timestamp to force bypass cache
    const timestamp = Date.now();
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('t', timestamp.toString());
    currentUrl.searchParams.set('v', APP_VERSION);
    
    window.location.href = currentUrl.toString();
  };

  const checkVersionMismatch = () => {
    const storedVersion = localStorage.getItem('app_version');
    return storedVersion !== APP_VERSION;
  };

  return {
    isClearing,
    cacheInfo,
    clearAllCaches,
    forceUpdate,
    checkVersionMismatch,
    currentVersion: APP_VERSION
  };
};